import { useEffect, useState } from "react";
import { useUser } from "../../../../../../contexts/userContext/userContext";
import useUserStorage from "../../../Reusable/useUserStorage";
import { useTransofrmedDate } from "../../../../../../utils/dateViewTransformer";
import { getDaysLeft } from "../../../../../../utils/getDaysLeft";
import { GRACE_PERIOD_IN_DAYS } from "../helpers/constants";

const DAYS_LEFT_NORMAL = 21;
const DAYS_LEFT_ATTENTION = 11;

/**
 * Custom hook that provides subscription information for the user.
 * It retrieves user data, storage information, and handles subscription-related actions.
 *
 * @returns An object containing subscription information and action handlers.
 */
const useSubscriptionInfo = () => {
  const { getUserData } = useUser();
  const { sizeInUse, sizeMax, percantegeOfSpace } = useUserStorage();
  const [isEmbedGraceSubscriber, setIsEmbedGraceSubscriber] = useState(false);
  const { returnDate } = useTransofrmedDate({
    date: isEmbedGraceSubscriber
      ? getUserData().EmbedExpireDate
      : getUserData().SubscribationExpirationDate,
    fullView: true,
    showDay: false,
  });
  const [graceDaysLeftForUser, setGraceDaysLeftForUser] = useState(0);
  const [isInGraceSubscriber, setIsInGraceSubscriber] = useState(false);
  const daysLeft = getDaysLeft(getUserData().SubscribationExpirationDate).days;
  let color: "green" | "orange" | "red" | undefined = undefined;
  if (daysLeft) {
    if (daysLeft >= DAYS_LEFT_NORMAL) {
      color = "green";
    } else if (daysLeft >= DAYS_LEFT_ATTENTION) {
      color = "orange";
    } else {
      color = "red";
    }
  }

  /**
   * Checks if the user is in grace period.
   * The user is in grace period if the difference between the expiration date
   * and the current date is less than 14 days.
   */
  useEffect(() => {
    let embedExpiredDate = new Date(getUserData().EmbedExpireDate);
    let subscriberExpiredDate = new Date(
      getUserData().SubscribationExpirationDate
    );
    let todayDate = new Date();

    // validate if user is or was embed
    if (embedExpiredDate) {
      const diffTime = todayDate.getTime() - embedExpiredDate.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const graceDaysLeft = (diffDays + GRACE_PERIOD_IN_DAYS) - 1;
      if (graceDaysLeft > 0 && graceDaysLeft < GRACE_PERIOD_IN_DAYS) {
        setIsEmbedGraceSubscriber(true);
        setGraceDaysLeftForUser(diffDays);
        setIsInGraceSubscriber(true);
      }
    }

    if (subscriberExpiredDate) {
      const diffTime = subscriberExpiredDate.getTime() - todayDate.getTime();
      let diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const graceDaysLeft = (diffDays + GRACE_PERIOD_IN_DAYS) - 1;
      if (graceDaysLeft > 0 && graceDaysLeft < GRACE_PERIOD_IN_DAYS) {
        setGraceDaysLeftForUser(graceDaysLeft);
        setIsInGraceSubscriber(true);
      }
    }
  }, [getUserData()]);

  /**
   * Checks if the user's subscription is an old subscriber package.
   * An old subscriber package is considered to be one of the following:
   * - JM Basic
   * - JM Standard
   * - JM Advanced
   */
  const isOldSubscriberPackage =
    getUserData().Subscription === "JM Basic" ||
    getUserData().Subscription === "JM Standard" ||
    getUserData().Subscription === "JM Advanced";

  /**
   * Checks if the expiration date of the subscription is shown.
   * The expiration date is shown if the difference between the expiration date
   * and the current date is less than 60 days,
   * and the user is not a sub-user or is an admin.
   */
  const isExpirationDateShown = (() => {
    const expDate = new Date(
      getUserData().SubscribationExpirationDate
    ).getTime();
    const nowDate = new Date().getTime();
    const diff = expDate - nowDate;
    const diffDays = diff / (1000 * 60 * 60 * 24);
    return (
      diffDays < 60 &&
      (!getUserData().IsSubUser || getUserData().subUserRole === "admin")
    );
  })();

  const getRenewSubscriptionPath = () => {
    const embedExpiredDate = getUserData().EmbedExpireDate;
    const subscriberExpiredDate = getUserData().SubscribationExpirationDate;

    if (
      (embedExpiredDate && isEmbedGraceSubscriber) ||
      (isInGraceSubscriber && subscriberExpiredDate)
    ) {
      if (getUserData().Subscription?.includes("Premium")) {
        return `purchase/premium/monthly`;
      } else if (getUserData().Subscription?.includes("Pro")) {
        return `purchase/pro/monthly`;
      } else {
        return `purchase/business/monthly`;
      }
    }
    return "";
  };

  return {
    isOldSubscriberPackage,
    isExpirationDateShown,
    isEmbedGraceSubscriber,
    graceDaysLeftForUser,
    isInGraceSubscriber,
    returnDate,
    color,
    sizeInUse,
    sizeMax,
    percentageOfSpace: percantegeOfSpace,
    daysLeft: daysLeft || 0,
    getRenewSubscriptionPath,
  };
};

export default useSubscriptionInfo;
